import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Api from '../api/userApi';
import { toast } from 'react-toastify';
import webPushService from '../utils/webPush';

const AuthContext = createContext();

const TOKEN_STORAGE_KEY = 'token';
const REFRESH_TOKEN_STORAGE_KEY = 'refreshToken';

// Minimal JWT payload decoder (no signature verification)
function decodeJwtPayload(token) {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function deriveUserFromToken(token) {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload !== 'object') return null;
  // Try common claim names - backend uses 'userId'
  const id = payload.userId || payload.sub || payload.id || null;
  const email = payload.email || null;
  const name = payload.name || payload.fullName || payload.username || null;
  const roles = payload.roles || (payload.role ? [payload.role] : null);
  
  // If we have at least an ID, we can create a minimal user object
  if (!id) {
    return null;
  }
  
  return {
    id,
    _id: id, // MongoDB uses _id
    email,
    fullName: name,
    name,
    roles: roles || [],
    role: roles?.[0],
    isBasicProfileComplete: !!payload.isBasicProfileComplete,
    isWorkerProfileComplete: !!payload.isWorkerProfileComplete,
    // mark this object as provisional so UI can handle missing fields if needed
    isProvisional: true
  };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Configure axios defaults - use the same server as userApi
const envBase = process.env.REACT_APP_API_BASE_URL;
const localWebServer = 'http://localhost:5001';
const localLanServer = 'http://192.168.1.37:5001';

// Use environment variable if set, otherwise detect based on current origin
// In production, if no env var is set, try to infer from current origin
let server = envBase;
if (!server) {
  if (typeof window !== 'undefined') {
    // In browser, check if we're on production domain
    const currentOrigin = window.location.origin;
    if (currentOrigin === 'https://www.ulcaapp.com') {
      // Production frontend - use production API
      server = 'https://www.ulcaapp.com';
    } else if (currentOrigin === 'https://ulcaapp.testatozas.in') {
      // Test/staging frontend - use test API
      server = 'https://ulcaapp.testatozas.in';
    } else {
      // Development - use localhost
      server = localWebServer;
    }
  } else {
    // Server-side rendering - use LAN server
    server = localLanServer;
  }
}

axios.defaults.baseURL = `${server}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Keep a lightweight, always-available view of whether the user currently has any services/skills.
  // This is used by global UI like the navbar and must react immediately to adds/removes.
  const [workerSkillsCount, setWorkerSkillsCount] = useState(0);
  const [hasToken, setHasToken] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return !!localStorage.getItem(TOKEN_STORAGE_KEY);
    } catch {
      return false;
    }
  });

  const updateWorkerSkillsCount = useCallback((count) => {
    const n = Number.isFinite(Number(count)) ? Number(count) : 0;
    setWorkerSkillsCount(Math.max(0, n));
  }, []);

  const refreshWorkerSkillsCount = useCallback(async () => {
    try {
      const response = await axios.get(Api.workerProfile);
      const skills = response.data?.worker?.skills || [];
      updateWorkerSkillsCount(Array.isArray(skills) ? skills.length : 0);
      return { success: true };
    } catch (error) {
      // If the user can't access worker profile (or it doesn't exist yet), treat as no skills.
      updateWorkerSkillsCount(0);
      return { success: false };
    }
  }, [updateWorkerSkillsCount]);

  // Helper to persist tokens and keep hasToken state in sync
  const setStoredTokens = useCallback((accessToken, refreshToken) => {
    try {
      if (accessToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, accessToken);
        localStorage.setItem('hasLoggedInBefore', 'true');
        axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        setHasToken(true);
      }
      if (refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      }
    } catch {
      // Ignore storage errors (e.g. in private mode)
    }
  }, []);

  const clearStoredTokens = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    } catch {
      // Ignore storage errors
    }
    delete axios.defaults.headers.common['Authorization'];
    setHasToken(false);
  }, []);

  // On initial mount, restore auth state from token in localStorage (if present)
  useEffect(() => {
    let accessToken = null;
    let refreshToken = null;

    try {
      accessToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
      setHasToken(!!accessToken);
    } catch {
      accessToken = null;
      refreshToken = null;
      setHasToken(false);
    }

    // If no local tokens, try to restore an existing ATOZAS SSO session.
    // On application startup (and right after the ATOZAS callback redirect),
    // /auth/atozas/me returns the application's normal JWT when an ATOZAS
    // session exists. When ATOZAS SSO is disabled it simply returns
    // { authenticated: false } and we stay a guest. (baseURL:'' bypasses the
    // axios /api prefix since these routes are mounted at /auth.)
    if (!accessToken && !refreshToken) {
      (async () => {
        try {
          const meResp = await axios.get(Api.atozasMe, { withCredentials: true, baseURL: '' });
          if (meResp.data?.authenticated) {
            const { token, accessToken: atozasAccess, refreshToken: atozasRefresh, user: atozasUser } = meResp.data;
            const finalToken = atozasAccess || token;
            if (finalToken) {
              setStoredTokens(finalToken, atozasRefresh);
              if (atozasUser) setUser(atozasUser);
              webPushService.syncSubscriptionForUser({ reason: 'atozas-sso' }).catch(() => {});
            }
          }
        } catch {
          // No ATOZAS session (or SSO disabled) — remain unauthenticated.
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // Set access token in axios headers if available
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }

    (async () => {
      try {
        // Try to get full user profile from backend using access token
        const response = await axios.get(Api.userProfile);
        if (response.data?.user) {
          setUser(response.data.user);
          // If backend already returned workerProfile, use it immediately (prevents navbar label flicker).
          const workerProfile = response.data?.workerProfile;
          if (workerProfile && Array.isArray(workerProfile.skills)) {
            updateWorkerSkillsCount(workerProfile.skills.length);
          } else {
            // Fallback: fetch worker profile separately (covers owners and any edge-cases).
            refreshWorkerSkillsCount();
          }
          setLoading(false);
          return;
        }
        // Fallback: derive minimal user info from token
        const provisional = deriveUserFromToken(accessToken);
        if (provisional) {
          setUser(provisional);
        }
        setLoading(false);
      } catch (error) {
        // If access token is expired (401), try to refresh it
        if (error.response?.status === 401 && refreshToken) {
          try {
            console.log('Access token expired, attempting to refresh...');
            const refreshResponse = await axios.post(Api.authRefreshToken, {
              refreshToken: refreshToken
            });

            const {
              token,
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
              user: refreshedUser
            } = refreshResponse.data || {};

            const finalAccessToken = newAccessToken || token;
            if (finalAccessToken) {
              // Successfully refreshed - store new tokens
              setStoredTokens(finalAccessToken, newRefreshToken);
              if (refreshedUser) {
                setUser(refreshedUser);
              } else {
                // Try to get user profile with new token
                try {
                  const profileResponse = await axios.get(Api.userProfile);
                  if (profileResponse.data?.user) {
                    setUser(profileResponse.data.user);
                    refreshWorkerSkillsCount();
                  }
                } catch {
                  // Fallback to token-derived user
                  const provisional = deriveUserFromToken(finalAccessToken);
                  if (provisional) {
                    setUser(provisional);
                    refreshWorkerSkillsCount();
                  }
                }
              }
              setLoading(false);
              return;
            }
          } catch (refreshError) {
            const refreshStatus = refreshError?.response?.status;
            if (refreshStatus === 401 || refreshStatus === 403) {
              // Refresh token definitively invalid/expired/revoked — clear session.
              console.warn('Token refresh failed (refresh token invalid/expired) — clearing stored tokens');
              clearStoredTokens();
            } else {
              // Transient failure (network/timeout/5xx/backend restarting): KEEP
              // the tokens so the session survives. Fall back to a token-derived
              // user so the app stays usable; a later request/startup retries refresh.
              console.warn('Token refresh failed transiently on startup — keeping tokens; using token-derived user for now:', refreshError?.message || refreshError);
              const provisional = deriveUserFromToken(accessToken);
              if (provisional) {
                setUser(provisional);
              }
            }
            setLoading(false);
            return;
          }
        }

        // For other errors (network issues, 500 errors), keep the token and use fallback
        if (error.response?.status !== 401) {
          console.warn('Failed to fetch user profile, using token fallback:', error.message);
          const provisional = deriveUserFromToken(accessToken);
          if (provisional) {
            setUser(provisional);
            // Try to fetch full profile in the background (silently)
            axios.get(Api.userProfile)
              .then(response => {
                if (response.data?.user) {
                  setUser(response.data.user);
                  refreshWorkerSkillsCount();
                }
              })
              .catch(() => {
                // Silently fail - we already have the fallback user
              });
          } else {
            // If we can't even decode the token, it might be corrupted
            console.warn('Cannot decode token, clearing it');
            clearStoredTokens();
          }
        } else {
          // 401 and no refresh token available - clear everything
          console.warn('Access token invalid and no refresh token available');
          clearStoredTokens();
        }
        setLoading(false);
      }
    })();
  }, [clearStoredTokens, refreshWorkerSkillsCount, setStoredTokens, updateWorkerSkillsCount]);

  // When auth user changes (login/register/logout/refresh), refresh worker skills in background.
  useEffect(() => {
    if (!user) {
      updateWorkerSkillsCount(0);
      return;
    }
    refreshWorkerSkillsCount();
  }, [user, refreshWorkerSkillsCount, updateWorkerSkillsCount]);

  // Attach a global axios response interceptor to handle automatic token refresh
  useEffect(() => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach(prom => {
        if (error) {
          prom.reject(error);
        } else {
          prom.resolve(token);
        }
      });
      failedQueue = [];
    };

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};

        // If request was already retried or there's no response, just reject
        if (!error.response || originalRequest._retry) {
          return Promise.reject(error);
        }

        const status = error.response.status;

        // Only handle 401 errors for routes that are not auth/refresh endpoints
        const isAuthRoute =
          originalRequest.url?.includes('/auth/login') ||
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh-token') ||
          originalRequest.url?.includes('/auth/email-otp');

        if (status === 401 && !isAuthRoute) {
          // If we're already refreshing, queue this request
          if (isRefreshing) {
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            })
              .then(token => {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers['Authorization'] = `Bearer ${token}`;
                return axios(originalRequest);
              })
              .catch(err => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
          if (!storedRefreshToken) {
            // No refresh token at all — the session cannot be recovered, so this
            // is a definitive logout (not a transient failure).
            isRefreshing = false;
            processQueue(error, null);
            clearStoredTokens();
            setUser(null);
            return Promise.reject(error);
          }

          try {
            const refreshResponse = await axios.post(Api.authRefreshToken, {
              refreshToken: storedRefreshToken
            });

            const {
              token,
              accessToken,
              refreshToken,
              user: refreshedUser
            } = refreshResponse.data || {};

            const newAccessToken = accessToken || token;
            if (!newAccessToken) {
              throw new Error('No access token returned from refresh endpoint');
            }

            setStoredTokens(newAccessToken, refreshToken);

            if (refreshedUser) {
              setUser(refreshedUser);
            }

            // Update the Authorization header for the original request and retry it
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;

            // Process queued requests
            processQueue(null, newAccessToken);
            isRefreshing = false;

            return axios(originalRequest);
          } catch (refreshError) {
            // Process queued requests with error
            processQueue(refreshError, null);
            isRefreshing = false;

            // ONLY log out when the refresh endpoint DEFINITIVELY rejects the
            // refresh token (invalid / expired / revoked / account deactivated),
            // which the backend signals with 401/403. Transient failures
            // (network error, timeout, 5xx, backend restarting during a deploy,
            // DB-not-ready) MUST keep the tokens so a later request/startup can
            // retry the refresh and the user stays logged in.
            const refreshStatus = refreshError?.response?.status;
            if (refreshStatus === 401 || refreshStatus === 403) {
              console.warn('Refresh token invalid/expired/revoked — logging out');
              clearStoredTokens();
              setUser(null);
            } else {
              console.warn('Token refresh failed transiently — keeping tokens for later retry:', refreshError?.message || refreshError);
            }
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, [clearStoredTokens, setStoredTokens]);

  const login = async (identifier, password) => {
    try {
      const response = await axios.post(Api.authLogin, {
        identifier,
        password
      });

      // Check if response has the expected structure
      if (!response.data) {
        console.error('Login response missing data:', response);
        toast.error('Invalid response from server');
        return { success: false, error: 'Invalid response from server' };
      }

      const { token, accessToken, refreshToken, user } = response.data;
      
      // Validate that we received a token
      const finalAccessToken = accessToken || token;
      if (!finalAccessToken) {
        console.error('Login response missing access token:', response.data);
        toast.error('Login failed: No token received');
        return { success: false, error: 'No token received from server' };
      }

      // Validate that we received user data
      if (!user) {
        console.error('Login response missing user:', response.data);
        toast.error('Login failed: No user data received');
        return { success: false, error: 'No user data received from server' };
      }
      
      setStoredTokens(finalAccessToken, refreshToken);
      setUser(user);
      refreshWorkerSkillsCount();

      console.log('[Auth] ✅ Login successful for user:', user?._id || user?.id, user?.email || user?.phone || '');
      // Bind THIS browser's push subscription to the just-logged-in user so
      // notifications are delivered to the correct account (and never to a
      // previous user who used this same browser).
      webPushService.syncSubscriptionForUser({ reason: 'login' }).catch((err) =>
        console.warn('[Auth] Push subscription sync after login failed (non-fatal):', err?.message || err)
      );

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('Login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      
      let message = 'Login failed';
      
      if (error.response) {
        // Server responded with error
        message = error.response.data?.message || 
                  error.response.data?.error || 
                  `Login failed: ${error.response.status} ${error.response.statusText}`;
      } else if (error.request) {
        // Request made but no response
        message = 'No response from server. Please check your connection.';
      } else {
        // Error setting up request
        message = error.message || 'Login failed';
      }
      
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const loginWithEmailOtp = async (email, otp, fullName) => {
    try {
      const payload = { email, emailOtp: otp };
      if (fullName && fullName.trim()) payload.fullName = fullName.trim();
      const response = await axios.post(Api.authLoginEmailOtp, payload);

      if (!response.data) {
        console.error('OTP login response missing data:', response);
        toast.error('Invalid response from server');
        return { success: false, error: 'Invalid response from server' };
      }

      const { token, accessToken, refreshToken, user } = response.data;
      const finalAccessToken = accessToken || token;

      if (!finalAccessToken) {
        console.error('OTP login response missing access token:', response.data);
        toast.error('Login failed: No token received');
        return { success: false, error: 'No token received from server' };
      }

      if (!user) {
        console.error('OTP login response missing user:', response.data);
        toast.error('Login failed: No user data received');
        return { success: false, error: 'No user data received from server' };
      }

      setStoredTokens(finalAccessToken, refreshToken);
      setUser(user);
      refreshWorkerSkillsCount();

      console.log('[Auth] ✅ OTP login successful for user:', user?._id || user?.id, user?.email || user?.phone || '');
      // Bind this browser's push subscription to the logged-in user.
      webPushService.syncSubscriptionForUser({ reason: 'login-otp' }).catch((err) =>
        console.warn('[Auth] Push subscription sync after OTP login failed (non-fatal):', err?.message || err)
      );

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.error('OTP login error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });

      let message = 'Login failed';

      if (error.response) {
        message = error.response.data?.message ||
                  error.response.data?.error ||
                  `Login failed: ${error.response.status} ${error.response.statusText}`;
      } else if (error.request) {
        message = 'No response from server. Please check your connection.';
      } else {
        message = error.message || 'Login failed';
      }

      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(Api.authRegister, userData);
      
      const { token, accessToken, refreshToken, user } = response.data;

      const finalAccessToken = accessToken || token;
      if (!finalAccessToken) {
        console.error('Registration response missing access token:', response.data);
        toast.error('Registration failed: No token received');
        return { success: false, error: 'No token received from server' };
      }
      
      setStoredTokens(finalAccessToken, refreshToken);
      setUser(user);
      refreshWorkerSkillsCount();

      console.log('[Auth] ✅ Registration successful for user:', user?._id || user?.id, user?.email || user?.phone || '');
      // Bind this browser's push subscription to the newly registered user.
      webPushService.syncSubscriptionForUser({ reason: 'register' }).catch((err) =>
        console.warn('[Auth] Push subscription sync after registration failed (non-fatal):', err?.message || err)
      );

      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    const currentUserId = user?._id || user?.id;
    console.log('[Auth] 🚪 Logging out user:', currentUserId || 'unknown');

    // Push notifications intentionally SURVIVE logout. We keep the browser
    // PushSubscription and its server record (user + endpoint + keys + isActive)
    // so the user can still receive notifications while logged out (even with the
    // PWA closed). We ONLY reset the push service's in-memory state here; the
    // endpoint is re-bound to the next user via /subscribe when they log in on
    // this device. Deactivating the subscription is reserved for the explicit
    // "turn off notifications on this device" toggle — never for logout.
    try {
      webPushService.clearSubscriptionForLogout();
    } catch (err) {
      console.warn('[Auth] Push state reset on logout failed (non-fatal):', err?.message || err);
    }

    // Also destroy any server-side ATOZAS SSO session so a page refresh does
    // not silently restore the session via /auth/atozas/me. Best-effort — never
    // blocks or breaks the existing client-side logout.
    try {
      axios.post(Api.atozasLogout, {}, { withCredentials: true, baseURL: '' }).catch(() => {});
    } catch {
      // ignore
    }

    clearStoredTokens();
    setUser(null);
    updateWorkerSkillsCount(0);
    toast.info('Logged out successfully');
  };

  const verifyPhone = async (phone, otp) => {
    try {
      await axios.post(Api.authVerifyPhone, {
        phone,
        otp
      });
      
      // Update user state
      setUser(prev => ({
        ...prev,
        isPhoneVerified: true
      }));
      
      toast.success('Phone verified successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Phone verification failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resendOTP = async (phone) => {
    try {
      await axios.post(Api.authResendOtp, { phone });
      toast.success('OTP sent successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send OTP';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(Api.userProfile, profileData);
      setUser(response.data.user);
      toast.success('Profile updated successfully!');
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(Api.userProfile);
      setUser(response.data.user);
      const workerProfile = response.data?.workerProfile;
      if (workerProfile && Array.isArray(workerProfile.skills)) {
        updateWorkerSkillsCount(workerProfile.skills.length);
      } else {
        refreshWorkerSkillsCount();
      }
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return { success: false };
    }
  };

  const completeBasicProfile = async (profileData) => {
    try {
      const response = await axios.put(Api.userCompleteBasicProfile, profileData);
      setUser(response.data.user);
      toast.success('Profile completed successfully!');
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Profile completion failed';
      toast.error(message);
      return { success: false, error: message, errors: error.response?.data?.errors };
    }
  };

  const completeWorkerProfile = async (profileData) => {
    try {
      const response = await axios.put(Api.userCompleteWorkerProfile, profileData);
      setUser(response.data.user);
      refreshWorkerSkillsCount();
      toast.success('Worker profile completed successfully!');
      return { success: true, user: response.data.user };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Worker profile completion failed';
      toast.error(message);
      return { success: false, error: message, errors: error.response?.data?.errors };
    }
  };

  const value = {
    user,
    loading,
    hasToken,
    workerSkillsCount,
    hasActiveServices: workerSkillsCount > 0,
    isBasicProfileComplete: !!user?.isBasicProfileComplete,
    isWorkerProfileComplete: !!user?.isWorkerProfileComplete,
    // Treat a valid stored token as authenticated; user will hydrate from API / token decode
    isAuthenticated: !!user || hasToken,
    login,
    loginWithEmailOtp,
    register,
    logout,
    verifyPhone,
    resendOTP,
    updateProfile,
    refreshUser,
    refreshWorkerSkillsCount,
    updateWorkerSkillsCount,
    completeBasicProfile,
    completeWorkerProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};